/**
 * MFA Backup Codes Hook
 * 
 * Manages Multi-Factor Authentication (MFA) backup codes for the current user.
 * Backup codes provide a recovery mechanism when the primary MFA device is
 * unavailable, ensuring users never lose access to their accounts.
 * 
 * **Key Features:**
 * - Fetch count of remaining (unused) backup codes
 * - Monitor backup code usage
 * - Refresh backup code status
 * - Alert when running low on codes
 * 
 * **Backup Code Lifecycle:**
 * 1. Codes generated during MFA enrollment (typically 10 codes)
 * 2. Each code can be used only once
 * 3. Codes are marked with `used_at` timestamp when redeemed
 * 4. User should regenerate codes when supply is low
 * 
 * **Security Best Practices:**
 * - Backup codes should be stored securely offline
 * - Each code is single-use to prevent replay attacks
 * - Users should be warned when codes are running low
 * - Regeneration requires re-authentication
 * 
 * **Use Cases:**
 * - Display backup code status in security settings
 * - Alert users when codes are depleted
 * - Guide users through backup code regeneration
 * - Recovery flow when MFA device is lost
 * 
 * @returns {Object} MFA backup codes state
 * @returns {number} remainingCodes - Count of unused backup codes
 * @returns {boolean} isLoading - Loading state
 * @returns {Function} refreshCodes - Manually refresh backup code count
 * 
 * @example
 * // Display backup code status
 * const { remainingCodes, isLoading, refreshCodes } = useMFABackupCodes();
 * 
 * if (remainingCodes < 3) {
 *   return (
 *     <Alert variant="warning">
 *       Only {remainingCodes} backup codes remaining.
 *       <Button onClick={regenerateCodes}>Generate New Codes</Button>
 *     </Alert>
 *   );
 * }
 * 
 * @example
 * // Refresh after code generation
 * await generateNewBackupCodes();
 * await refreshCodes();
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export function useMFABackupCodes() {
  const { user } = useAuth();
  const [remainingCodes, setRemainingCodes] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchRemainingCodes();
    }
  }, [user?.id]);

  const fetchRemainingCodes = async () => {
    if (!user?.id) return;

    try {
      const { count, error } = await supabase
        .from('mfa_backup_codes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('used_at', null);

      if (error) throw error;
      setRemainingCodes(count || 0);
    } catch (error) {
      console.error('Error fetching backup codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    remainingCodes,
    isLoading,
    refreshCodes: fetchRemainingCodes,
  };
}
